import { isDriveId } from "./drive";
import { sanitizeHtml } from "./sanitizeHtml";

/**
 * Files in a chat message travel as a marker inside the message HTML, so the
 * one message pipeline (1:1, group chat and channel alike — plus Mail's chat
 * dock, search and the chat-list preview) carries them without a schema change:
 *
 *   <div data-athavita-file="<driveItemId>" data-name="report.pdf"
 *        data-size="12345" data-type="application/pdf" data-owner="a@b.com">📎 report.pdf</div>
 *
 * DOMPurify keeps data-* attributes by default (ALLOW_DATA_ATTR), and the
 * "📎 name" text is what older clients and the chat list show. Only the id is
 * trusted, and only after it validates as a Drive id; name/size/type/owner are
 * display hints until the card resolves the real item from Drive. No URL is
 * ever read from a marker.
 */
export interface FileRef {
    id: string;
    name: string;
    size: number;
    type: string;
    owner: string;
}

const MARKER_ATTR = "data-athavita-file";

function escapeAttr(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function escapeText(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function fileMarkerHtml(ref: FileRef): string {
    return `<div ${MARKER_ATTR}="${escapeAttr(ref.id)}" data-name="${escapeAttr(ref.name)}" data-size="${Math.max(0, Math.round(ref.size))}" data-type="${escapeAttr(ref.type)}" data-owner="${escapeAttr(ref.owner)}">📎 ${escapeText(ref.name)}</div>`;
}

/**
 * Sanitise a message body and pull its file markers out. Returns the rest of
 * the HTML (sanitised again after the markers are removed) and the validated
 * file references, in message order.
 */
export function splitFileMarkers(html: string | null | undefined): { html: string; files: FileRef[] } {
    const clean = sanitizeHtml(html);
    if (!clean.includes(MARKER_ATTR)) return { html: clean, files: [] };
    // DOMParser documents are inert — nothing runs and nothing loads.
    const doc = new DOMParser().parseFromString(clean, "text/html");
    const files: FileRef[] = [];
    const seen = new Set<string>();
    for (const el of Array.from(doc.body.querySelectorAll(`[${MARKER_ATTR}]`))) {
        const id = el.getAttribute(MARKER_ATTR) ?? "";
        el.remove();
        if (!isDriveId(id) || seen.has(id.toLowerCase())) continue;
        seen.add(id.toLowerCase());
        const size = Number.parseInt(el.getAttribute("data-size") ?? "", 10);
        files.push({
            id,
            name: (el.getAttribute("data-name") ?? "").slice(0, 512) || "File",
            size: Number.isFinite(size) && size >= 0 ? size : 0,
            type: (el.getAttribute("data-type") ?? "").slice(0, 128),
            owner: (el.getAttribute("data-owner") ?? "").slice(0, 256)
        });
    }
    return { html: sanitizeHtml(doc.body.innerHTML), files };
}

/** The markers of a message, rebuilt — so an edit that rewrites the text keeps its files. */
export function fileMarkersOf(html: string | null | undefined): string {
    return splitFileMarkers(html).files.map(fileMarkerHtml).join("");
}

/** True when the visible HTML is empty (no text, no images) — a files-only message. */
export function isBlankHtml(html: string): boolean {
    if (!html) return true;
    if (/<img\b/i.test(html)) return false;
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent ?? "").trim().length === 0;
}
